import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ThemeProvider } from './contexts/ThemeContext';
import { TranslationProvider } from './contexts/TranslationContext';
import { UserProvider, useUser } from './contexts/UserContext';
import { ToastProvider } from './contexts/ToastContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Sidebar from './components/Sidebar';
import PartnerPageLayout from './components/layout/PartnerPageLayout';
import Dashboard from './pages/Dashboard';
import Affiliates from './pages/Affiliates';
import AffiliateTransfer from './pages/AffiliateTransfer';
import Withdrawal from './pages/Withdrawal';
import PayoutHistory from './pages/PayoutHistory';
import Commissions from './pages/Commissions';
import Clients from './pages/Clients';
import CommissionReport from './pages/CommissionReport';
import ClientCampaigns from './pages/ClientCampaigns';
import SubAffiliateCampaigns from './pages/SubAffiliateCampaigns';
import Banners from './pages/Banners';
import SubAffiliateBanners from './pages/SubAffiliateBanners';
import PartnerLinks from './pages/PartnerLinks';
import SubPartnerLinks from './pages/SubPartnerLinks';
import PromotionList from './pages/promotions/PromotionList';
import MyPromotions from './pages/promotions/MyPromotions';
import ChallengesList from './pages/challenges/ChallengesList';
import Events from './pages/Events';
import RewardsDashboard from './pages/rewards/RewardsDashboard';
import ClaimRewards from './pages/rewards/ClaimRewards';
import RewardsHowItWorks from './pages/rewards/RewardsHowItWorks';
import RewardsReport from './pages/rewards/RewardsReport';
import LoyaltyDashboard from './pages/loyalty/LoyaltyDashboard';
import LoyaltyEarn from './pages/loyalty/LoyaltyEarn';
import LoyaltyHistory from './pages/loyalty/LoyaltyHistory';
import AccountTierProgress from './pages/accountTier/AccountTierProgress';
import AccountTierHowItWorks from './pages/accountTier/AccountTierHowItWorks';
import InstrumentsCommission from './pages/instruments/InstrumentsCommission';
import KycHub from './pages/kyc/KycHub';
import NotificationsPage from './pages/notifications/NotificationsPage';
import ReferAFriend from './pages/refer/ReferAFriend';
import SubCommission from './pages/subCommission/SubCommission';
import Courses from './pages/education/Courses';
import CourseForm from './pages/education/CourseForm';
import CourseDetails from './pages/education/CourseDetails';
import ModuleLessons from './pages/education/ModuleLessons';
import LessonContent from './pages/education/LessonContent';
import EducationAnalytics from './pages/education/EducationAnalytics';
import ClientQr from './pages/qr/ClientQr';
import PartnerQr from './pages/qr/PartnerQr';
import Profile from './pages/Profile';
import Support from './pages/Support';
import AddWithdrawalDetails from './pages/AddWithdrawalDetails';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import WhatsAppChatButton from './components/common/WhatsAppChatButton';
import { performLogout } from './utils/logout';



function AppContent() {
  const { clearUserData } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize isLoggedIn from localStorage synchronously to prevent flash
  const [isLoggedIn, setIsLoggedIn] = useState(() => {
    const loginStatus = localStorage.getItem('isLoggedIn');
    const authToken = localStorage.getItem('authToken');
    return loginStatus === 'true' && authToken && authToken.trim() !== '';
  });
  const [showUserDropdown, setShowUserDropdown] = useState(false);

  // Apply global light scrollbar styling
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      * {
        scrollbar-width: thin;
        scrollbar-color: #c4c9d2 #f3f4f6;
      }
      
      *::-webkit-scrollbar {
        width: 8px;
        height: 8px;
      }
      
      *::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 4px;
      }
      
      *::-webkit-scrollbar-thumb {
        background: #c4c9d2;
        border-radius: 4px;
      }
      
      *::-webkit-scrollbar-thumb:hover {
        background: #9ca3af;
      }
      
      *::-webkit-scrollbar-corner {
        background: #f3f4f6;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Check if user is logged in on component mount
  useEffect(() => {
    const checkAuthStatus = async () => {
      const loginStatus = localStorage.getItem('isLoggedIn');
      const authToken = localStorage.getItem('authToken');
      
      // Check both login status and token
      const isAuth = loginStatus === 'true' && authToken && authToken.trim() !== '';
      
      if (isAuth) {
        // Set logged in state immediately to avoid flash
        setIsLoggedIn(true);
        
        // Token validation will be handled by Dashboard APIs
        // No need for separate getUserProfile call
      } else {
        setIsLoggedIn(false);
      }
    };
    
    checkAuthStatus();
    
    // Listen for login state changes
    const handleLoginStateChange = () => {
      const loginStatus = localStorage.getItem('isLoggedIn');
      const authToken = localStorage.getItem('authToken');
      const isAuth = loginStatus === 'true' && authToken && authToken.trim() !== '';
      setIsLoggedIn(isAuth);
    };
    
    window.addEventListener('loginStateChanged', handleLoginStateChange);
    
    return () => {
      window.removeEventListener('loginStateChanged', handleLoginStateChange);
    };
  }, []);

  // Global error handler for token expiration
  useEffect(() => {
    const handleGlobalError = (event) => {
      // Check if error is related to authentication
      if (event.error && event.error.message && 
          (event.error.message.includes('Authentication failed') || 
           event.error.message.includes('401') ||
           event.error.message.includes('Unauthorized'))) {
        // Token expired
        handleLogout();
      }
    };

    // Listen for unhandled promise rejections (API errors)
    window.addEventListener('unhandledrejection', handleGlobalError);
    
    // Listen for global errors
    window.addEventListener('error', handleGlobalError);

    return () => {
      window.removeEventListener('unhandledrejection', handleGlobalError);
      window.removeEventListener('error', handleGlobalError);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only global listeners
  }, []);

  // Save current route to localStorage whenever route changes
  useEffect(() => {
    if (isLoggedIn && location.pathname !== '/login' && location.pathname !== '/register' && location.pathname !== '/forgot-password') {
      localStorage.setItem('currentRoute', location.pathname);
    }
  }, [location.pathname, isLoggedIn]);

  // Restore route on page refresh - run only once on mount
  useEffect(() => {
    if (isLoggedIn) {
      const savedRoute = localStorage.getItem('currentRoute');
      // Only navigate if we have a saved route and we're currently at root
      if (savedRoute && savedRoute !== '/' && location.pathname === '/') {
        navigate(savedRoute, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  const handleLogout = () => {
    performLogout(clearUserData);
    setIsLoggedIn(false);
    setShowUserDropdown(false);
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showUserDropdown) {
        const dropdowns = document.querySelectorAll('.relative');
        let clickedInside = false;
        
        dropdowns.forEach(dropdown => {
          if (dropdown.contains(event.target)) {
            clickedInside = true;
          }
        });
        
        if (!clickedInside) {
          setShowUserDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserDropdown]);



  return (
    <NotificationProvider enabled={isLoggedIn}>
      <div className="app-frame flex flex-col bg-[#F4F6F9]">
        {isLoggedIn ? (
          <div className="flex min-h-0 flex-1 overflow-hidden bg-[#F4F6F9]">
            <Sidebar />
            <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain [-webkit-overflow-scrolling:touch]">
              <Routes>
                <Route element={<PartnerPageLayout />}>
                  <Route path="/" element={<Dashboard />} />
                  <Route path="/affiliates" element={<Navigate to="/clients/sub-partners" replace />} />
                  <Route path="/affiliate/transfer" element={<AffiliateTransfer />} />
                  <Route path="/payout-history" element={<PayoutHistory />} />
                  <Route path="/withdrawal" element={<Withdrawal />} />
                  <Route path="/earnings/dashboard" element={<Commissions key="earnings-dashboard" view="overview" />} />
                  <Route path="/earnings/list" element={<Commissions key="earnings-list" view="list" />} />
                  <Route path="/earnings/list/old" element={<Commissions key="earnings-old-list" view="old-list" />} />
                  <Route path="/earnings/overview" element={<Navigate to="/earnings/dashboard" replace />} />
                  <Route
                    path="/earnings/sub-partner-commission"
                    element={<SubCommission />}
                  />
                  <Route
                    path="/sub-commission"
                    element={<Navigate to="/earnings/sub-partner-commission" replace />}
                  />
                  <Route path="/commissions" element={<Navigate to="/earnings/dashboard" replace />} />
                  <Route path="/clients" element={<Clients />} />
                  <Route path="/clients/sub-partners" element={<Affiliates />} />
                  <Route path="/reports/sub-ibs" element={<Navigate to="/clients/sub-partners" replace />} />
                  <Route path="/reports/client-list" element={<Navigate to="/clients" replace />} />
                  <Route path="/reports/commission" element={<CommissionReport />} />
                  <Route path="/campaigns/client" element={<ClientCampaigns />} />
                  <Route path="/campaigns/sub-affiliate" element={<SubAffiliateCampaigns />} />
                  <Route path="/education/courses" element={<Courses />} />
                  <Route path="/education/courses/new" element={<CourseForm />} />
                  <Route path="/education/courses/:courseId/edit" element={<CourseForm />} />
                  <Route
                    path="/education/courses/:courseId/modules/:moduleId/lessons/:lessonId"
                    element={<LessonContent />}
                  />
                  <Route
                    path="/education/courses/:courseId/modules/:moduleId/lessons"
                    element={<ModuleLessons />}
                  />
                  <Route path="/education/courses/:courseId" element={<CourseDetails />} />
                  <Route path="/education/analytics" element={<EducationAnalytics />} />
                  <Route path="/marketing/banners" element={<Banners />} />
                  <Route path="/marketing/sub-affiliate-banners" element={<SubAffiliateBanners />} />
                  <Route path="/links/partner" element={<PartnerLinks />} />
                  <Route path="/links/sub-partner" element={<SubPartnerLinks />} />
                  <Route path="/promotions" element={<Navigate to="/promotions/list" replace />} />
                  <Route path="/promotions/list" element={<PromotionList />} />
                  <Route path="/promotions/my" element={<MyPromotions />} />
                  <Route path="/challenges" element={<Navigate to="/challenges/list" replace />} />
                  <Route path="/challenges/list" element={<ChallengesList />} />
                  <Route path="/events" element={<Events />} />
                  <Route path="/rewards" element={<Navigate to="/rewards/dashboard" replace />} />
                  <Route path="/rewards/dashboard" element={<RewardsDashboard />} />
                  <Route path="/rewards/claim" element={<ClaimRewards />} />
                  <Route path="/rewards/how-it-works" element={<RewardsHowItWorks />} />
                  <Route path="/rewards/report" element={<RewardsReport />} />
                  <Route path="/loyalty-points" element={<LoyaltyDashboard />} />
                  <Route path="/loyalty/earn" element={<LoyaltyEarn />} />
                  <Route path="/loyalty/history" element={<LoyaltyHistory />} />
                  <Route path="/account-tier" element={<Navigate to="/account-tier/progress" replace />} />
                  <Route path="/account-tier/progress" element={<AccountTierProgress />} />
                  <Route
                    path="/account-tier/overview"
                    element={<Navigate to="/account-tier/progress" replace />}
                  />
                  <Route path="/account-tier/how-it-works" element={<AccountTierHowItWorks />} />
                  <Route
                    path="/account-tier/instruments-commission"
                    element={<InstrumentsCommission />}
                  />
                  <Route
                    path="/instruments-commission"
                    element={<Navigate to="/account-tier/instruments-commission" replace />}
                  />
                  <Route path="/levels" element={<Navigate to="/account-tier/progress" replace />} />
                  <Route path="/kyc" element={<KycHub />} />
                  <Route path="/verification" element={<Navigate to="/kyc" replace />} />
                  <Route path="/kyc/personal" element={<Navigate to="/kyc" replace />} />
                  <Route path="/kyc/identity" element={<Navigate to="/kyc" replace />} />
                  <Route path="/kyc/address" element={<Navigate to="/kyc" replace />} />
                  <Route path="/kyc/review" element={<Navigate to="/kyc" replace />} />
                  <Route path="/notifications" element={<NotificationsPage />} />
                  <Route path="/refer-a-friend" element={<ReferAFriend />} />
                  <Route path="/qr-code" element={<Navigate to="/qr-code/client" replace />} />
                  <Route path="/qr-code/client" element={<ClientQr />} />
                  <Route path="/qr-code/partner" element={<PartnerQr />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/settings/security" element={<Profile />} />
                  <Route path="/support" element={<Support />} />
                  <Route path="/settings/withdrawal-details" element={<AddWithdrawalDetails />} />
                  <Route path="/settings" element={<Navigate to="/settings/withdrawal-details" replace />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Route>
              </Routes>
            </main>
          </div>
        ) : (
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        )}

        <WhatsAppChatButton />
      </div>
    </NotificationProvider>
  );
}

function App() {
  return (
    <Router>
      <ThemeProvider>
        <TranslationProvider>
          <UserProvider>
            <ToastProvider>
              <AppContent />
            </ToastProvider>
          </UserProvider>
        </TranslationProvider>
      </ThemeProvider>
    </Router>
  );
}

export default App;
